import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Filter, Download } from 'lucide-react'
import { useOrg } from '../context/OrgContext'
import { getBreachEvents } from '../api/endpoints'
import LoadingSpinner from '../components/LoadingSpinner'
import Button from '../components/Button'
import RiskScoreBadge from '../components/RiskScoreBadge'
import Table, { Column } from '../components/Table'
import { BreachEvent } from '../api/types'
import { format } from 'date-fns'

const BreachEvents: React.FC = () => {
  const { currentOrg } = useOrg()
  const [sourceFilter, setSourceFilter] = useState<'all' | 'hibp' | 'breachsense'>('all')
  const [minRiskScore, setMinRiskScore] = useState(0)

  const { data: events, isLoading } = useQuery({
    queryKey: ['breach-events', currentOrg?.id, sourceFilter, minRiskScore],
    queryFn: () =>
      getBreachEvents(currentOrg!.id, {
        source: sourceFilter === 'all' ? undefined : sourceFilter,
        min_risk_score: minRiskScore > 0 ? minRiskScore : undefined,
      }),
    enabled: !!currentOrg,
  })

  const handleExportCSV = () => {
    if (!events || events.length === 0) return

    const headers = ['Date', 'Source', 'Risk Score', 'Breach Name', 'Session Cookie', 'Infostealer', 'Action']
    const rows = events.map((event) => [
      format(new Date(event.created_at), 'yyyy-MM-dd HH:mm:ss'),
      event.source,
      event.risk_score,
      event.breach_name || '',
      event.session_cookie_found ? 'Yes' : 'No',
      event.infostealer_family || '',
      event.action_taken || '',
    ])

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `breach-events-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const columns: Column<BreachEvent>[] = [
    {
      key: 'created_at',
      header: 'Date',
      render: (event) => (
        <span className="text-text-primary text-sm">
          {format(new Date(event.created_at), 'MMM d, yyyy HH:mm')}
        </span>
      ),
      sortable: true,
    },
    {
      key: 'source',
      header: 'Source',
      render: (event) => (
        <span className={`text-xs px-2 py-1 rounded-full ${
          event.source === 'hibp' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'
        }`}>
          {event.source.toUpperCase()}
        </span>
      ),
    },
    {
      key: 'risk_score',
      header: 'Risk Score',
      render: (event) => <RiskScoreBadge score={event.risk_score} />,
      sortable: true,
    },
    {
      key: 'breach_name',
      header: 'Breach Name',
      render: (event) => (
        <span className="text-text-primary text-sm">{event.breach_name || 'Unknown'}</span>
      ),
    },
    {
      key: 'session_cookie',
      header: 'Session Cookie',
      render: (event) => (
        event.session_cookie_found ? (
          <span className="text-risk-critical text-sm font-semibold">Yes</span>
        ) : (
          <span className="text-text-muted text-sm">No</span>
        )
      ),
    },
    {
      key: 'infostealer',
      header: 'Infostealer',
      render: (event) => (
        <span className="text-text-muted text-sm">{event.infostealer_family || '-'}</span>
      ),
    },
    {
      key: 'action',
      header: 'Action Taken',
      render: (event) => (
        <span className="text-text-muted text-sm">{event.action_taken || 'None'}</span>
      ),
    },
  ]

  if (!currentOrg || isLoading) {
    return <LoadingSpinner fullPage message="Loading breach events..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Breach Events</h1>
          <p className="text-text-muted mt-1">View and filter detected breach events</p>
        </div>
        <Button
          variant="secondary"
          icon={<Download className="w-4 h-4" />}
          onClick={handleExportCSV}
          disabled={!events || events.length === 0}
        >
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter className="w-5 h-5 text-text-muted" />
          <h2 className="text-lg font-semibold text-text-primary">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Source
            </label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value as any)}
              className="w-full px-3 py-2 rounded-md bg-background-tertiary border border-zinc-700 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary"
            >
              <option value="all">All Sources</option>
              <option value="hibp">HIBP Only</option>
              <option value="breachsense">Breachsense Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Minimum Risk Score
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="10"
              value={minRiskScore}
              onChange={(e) => setMinRiskScore(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-text-muted mt-1">
              <span>0</span>
              <span className="font-semibold text-text-primary">{minRiskScore}</span>
              <span>100</span>
            </div>
          </div>
        </div>
      </div>

      {/* Events Table */}
      <Table
        columns={columns}
        data={events || []}
        keyExtractor={(event) => event.id}
        emptyMessage="No breach events found"
      />
    </div>
  )
}

export default BreachEvents
